// Office "Standby rota" page (Session ANY). Who is on call which week.
//
// WHAT REPLACED WHAT. Until now the only trace a standby week left anywhere was a checkbox on an
// engineer's timesheet, ticked AFTER the week was over so the retainer could be priced (Session
// ANH). Nothing said who was ON CALL, which for a company publishing a 24-hour service is the
// question that actually matters at 11pm on a Sunday. This page is that answer, set in advance.
//
// A LIST, NOT A DRAG-AND-DROP BOARD, deliberately. A calendar board is what a scheduling team with
// dozens of engineers needs; ELE has four, and the whole arrangement is legible as a table of the
// next few weeks with a name against each one. The two controls the office actually needs -- set the
// rotation, and change ONE week -- are a form and a per-row button.
//
// NOTHING IS GENERATED, so there is no "generate forward" button to press and no horizon to run out
// of. Every row below is RESOLVED on read from the pattern and the stored overrides, which is why
// changing the rotation changes every future week the moment it is saved, and why an override for
// one week provably cannot disturb any other (server/src/modules/standby/standbyRota.ts).
//
// NO CLASH CHECKING AGAINST LEAVE, because the platform holds no leave. There is no holiday,
// sickness or absence record anywhere in the schema (checked directly, Session ANY), so there is
// nothing to check a rota against; the office knows who is away and sets the override. Once leave
// tracking exists, this page is where the warning would go.
//
// SESSION AUV, design system Session 8 (People): PageHeader, Button for "Set up the rota" / "Change the rota", the
// shared Loader, StatusPill for the "This week" marker, and lib/format dates (a week prints "5 Oct 2026" where it
// printed "5 October 2026"). Presentation only. The neutral outline buttons (Send rota to engineers, Change this week,
// the rotation's Up, Down, Remove and Add controls) are left as they are: the Button component has no neutral variant.
import { useCallback, useEffect, useState } from "react";
import type { FormEvent } from "react";
import { CalendarClock, Mail, Pencil } from "lucide-react";
import { ApiError, api } from "../../lib/api";
import type { StandbyOverview, StandbyWeek } from "../../lib/api";
import { ModalShell, MODAL_ACTIONS, MODAL_PRIMARY_BTN, MODAL_SECONDARY_BTN } from "../../components/ModalShell";
import { ConfirmDeleteModal } from "../../components/ConfirmDeleteModal";
import { presentableServerMessage } from "../../lib/serverMessage";
import { Button } from "../../components/Button";
import { Loader } from "../../components/Loader";
import { PageHeader } from "../../components/PageHeader";
import { StatusPill } from "../../components/StatusPill";
import { formatDate } from "../../lib/format";
import { initialMobileLayout } from "../../lib/mobileLayout";
import { MobileCardList, MobileListCard } from "../../components/MobileListCard";
import { StandbyRotaSendModal } from "./StandbyRotaSendModal";

const fieldInputCls =
  "w-full rounded-ele border border-ele-purple-accent bg-ele-field-inset px-3 py-1.5 text-sm text-ele-strong outline-none focus:border-ele-green";
const fieldLabelCls = "mb-1 block font-menu text-xs uppercase tracking-wide text-ele-body";

/**
 * The server's refusals are already plain English (an engineer who is no longer active, an engineer
 * named twice), so they are shown VERBATIM rather than replaced with wording here that could drift
 * from the rule. presentableServerMessage is the shared gate: it returns null for anything that does
 * not read as a sentence, so a raw error code can never reach the office.
 */
function errText(err: unknown, fallback: string): string {
  return (err instanceof ApiError ? presentableServerMessage(err.message) : null) ?? fallback;
}

/** How a resolved week reads in the "Set by" column. Pure, so the three states are testable without a render. */
export function standbySourceLabel(week: StandbyWeek): string {
  if (week.source === "override") return week.engineerId ? "Set for this week" : "Set for this week: nobody";
  if (week.source === "rota") return "From the rota";
  return "Not set";
}

/** The name to show against a week. Pure. */
export function standbyEngineerLabel(week: StandbyWeek): string {
  if (week.engineerId && week.engineerName) return week.engineerName;
  if (week.source === "override") return "Nobody on standby";
  return "Nobody on standby";
}

/**
 * Session ANZ: has the engineer on call that week been told, and how? Pure, so the states are
 * testable without a render, like the two labels above.
 *
 * A FUTURE week that has not been notified is not a warning: the sweep tells an engineer at the
 * start of their own week, deliberately, so colouring next month red would train the office to
 * ignore the column. The state that IS a warning is the one where the platform tried and reached
 * them on nothing.
 */
export function standbyNotifiedLabel(week: StandbyWeek, thisWeek: string): { text: string; tone: "ok" | "warn" | "quiet" } {
  if (!week.engineerId) return { text: "", tone: "quiet" };
  if (!week.notified) {
    return week.weekStarting < thisWeek
      ? { text: "Was not told", tone: "warn" }
      : { text: week.weekStarting === thisWeek ? "Not told yet" : "Tells them that Monday", tone: "quiet" };
  }
  const parts: string[] = [];
  if (week.notified.pushDevices > 0) parts.push(week.notified.pushDevices === 1 ? "notification" : `notification (${week.notified.pushDevices} devices)`);
  if (week.notified.smsOutcome === "sent") parts.push("text message");
  if (parts.length === 0) return { text: "Could not reach them", tone: "warn" };
  return { text: `Told by ${parts.join(" and ")}`, tone: "ok" };
}

/** How long before the rotation comes back round to the first engineer, in plain English. Pure. */
export function cycleLabel(cycleWeeks: number): string {
  if (cycleWeeks <= 0) return "";
  if (cycleWeeks === 1) return "Comes back round every week.";
  return `Comes back round every ${cycleWeeks} weeks.`;
}

// The per-week control, written ONCE so the desktop table row and the mobile card cannot drift (and
// so the colour ratchet in appContrast.test.ts counts one button, not two). max-sm:min-h-11 is the
// 44px phone tap target and is inert from 640px up.
function ChangeWeekButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="rounded-ele border border-ele-purple-accent px-3 py-1.5 text-xs text-ele-body transition hover:border-ele-green hover:text-ele-green max-sm:min-h-11"
    >
      Change this week
    </button>
  );
}

// The "Set by" detail of one week (where the name came from, the reason, whether the engineer was
// told), shared by the table's Set by column and the mobile card.
function SetByDetails({ week, thisWeek }: { week: StandbyWeek; thisWeek: string }) {
  const n = standbyNotifiedLabel(week, thisWeek);
  return (
    <>
      {standbySourceLabel(week)}
      {week.reason ? <span className="block text-xs">{week.reason}</span> : null}
      {n.text ? (
        <span className={`block text-xs ${n.tone === "warn" ? "text-ele-danger" : n.tone === "ok" ? "text-ele-green" : "text-ele-muted"}`}>{n.text}</span>
      ) : null}
    </>
  );
}

interface RotaForm {
  startWeek: string;
  weeksPerTurn: string;
  engineerIds: string[];
  notes: string;
}

interface WeekEditor {
  week: StandbyWeek;
  engineerId: string; // "" = nobody on standby that week
  reason: string;
}

export function StandbyRotaPage() {
  const [data, setData] = useState<StandbyOverview | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");
  const [rotaEditor, setRotaEditor] = useState<RotaForm | null>(null);
  const [rotaError, setRotaError] = useState<string | null>(null);
  const [savingRota, setSavingRota] = useState(false);
  const [weekEditor, setWeekEditor] = useState<WeekEditor | null>(null);
  const [weekError, setWeekError] = useState<string | null>(null);
  const [savingWeek, setSavingWeek] = useState(false);
  const [confirmRemoveRota, setConfirmRemoveRota] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const [removing, setRemoving] = useState(false);
  // Session ATU: sending the rota to the engineers. Offered ONCE after a successful save, never sent
  // on a save: the office answers "Send now" or "Not now".
  const [sendOffer, setSendOffer] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  // Below md the weeks render as cards, chosen once at mount (src/lib/mobileLayout.ts).
  const [mobileCards] = useState(initialMobileLayout);

  const openWeekEditor = (w: StandbyWeek) => {
    setWeekError(null);
    setWeekEditor({ week: w, engineerId: w.engineerId ?? "", reason: w.reason ?? "" });
  };

  const load = useCallback(async () => {
    try {
      setData(await api.standby.overview());
      setState("ready");
    } catch {
      setState("error");
    }
  }, []);

  useEffect(() => {
    setState("loading");
    void load();
  }, [load]);

  function startRotaEdit() {
    if (!data) return;
    setRotaError(null);
    setRotaEditor({
      startWeek: data.rota?.startWeek ?? data.thisWeek,
      weeksPerTurn: String(data.rota?.weeksPerTurn ?? 1),
      // An existing rota opens with its own order; a new one opens with every active engineer, in
      // name order, which is the arrangement a four-engineer company most often wants and is one
      // click from any other.
      engineerIds: data.rota ? data.rota.members.map((m) => m.engineerId) : data.engineers.map((e) => e.id),
      notes: data.rota?.notes ?? "",
    });
  }

  function toggleMember(engineerId: string) {
    setRotaEditor((f) => {
      if (!f) return f;
      return f.engineerIds.includes(engineerId)
        ? { ...f, engineerIds: f.engineerIds.filter((id) => id !== engineerId) }
        : { ...f, engineerIds: [...f.engineerIds, engineerId] };
    });
  }

  // The rotation ORDER is the whole pattern, so it has to be editable. Two buttons per row rather
  // than a drag handle: four names do not need a drag library, and the arrows work on a phone.
  function moveMember(engineerId: string, delta: -1 | 1) {
    setRotaEditor((f) => {
      if (!f) return f;
      const ids = [...f.engineerIds];
      const i = ids.indexOf(engineerId);
      const j = i + delta;
      if (i < 0 || j < 0 || j >= ids.length) return f;
      [ids[i], ids[j]] = [ids[j]!, ids[i]!];
      return { ...f, engineerIds: ids };
    });
  }

  async function saveRota(e: FormEvent) {
    e.preventDefault();
    if (!rotaEditor) return;
    const weeksPerTurn = Number(rotaEditor.weeksPerTurn);
    if (!Number.isInteger(weeksPerTurn) || weeksPerTurn < 1) {
      setRotaError("Enter how many weeks each engineer is on standby for as a whole number, for example 1.");
      return;
    }
    if (rotaEditor.engineerIds.length === 0) {
      setRotaError("Choose at least one engineer for the rotation. To switch the rota off entirely, remove it instead.");
      return;
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(rotaEditor.startWeek)) {
      setRotaError("Choose the week the rota starts from.");
      return;
    }
    setSavingRota(true);
    setRotaError(null);
    try {
      await api.standby.setRota({
        startWeek: rotaEditor.startWeek,
        weeksPerTurn,
        engineerIds: rotaEditor.engineerIds,
        notes: rotaEditor.notes.trim() === "" ? null : rotaEditor.notes.trim(),
      });
      setRotaEditor(null);
      setSendOffer(true);
      await load();
    } catch (err) {
      setRotaError(errText(err, "Could not save the rota. Please try again."));
    } finally {
      setSavingRota(false);
    }
  }

  async function saveWeek(e: FormEvent) {
    e.preventDefault();
    if (!weekEditor) return;
    setSavingWeek(true);
    setWeekError(null);
    try {
      await api.standby.setWeek(weekEditor.week.weekStarting, {
        engineerId: weekEditor.engineerId === "" ? null : weekEditor.engineerId,
        reason: weekEditor.reason.trim() === "" ? null : weekEditor.reason.trim(),
      });
      setWeekEditor(null);
      await load();
    } catch (err) {
      setWeekError(errText(err, "Could not save that week. Please try again."));
    } finally {
      setSavingWeek(false);
    }
  }

  // Clearing an override is NOT the same act as setting the week to nobody, and the two are worded
  // differently everywhere for that reason: this removes the office's decision so the week goes back
  // to whatever the rotation says.
  async function clearWeek() {
    if (!weekEditor) return;
    setSavingWeek(true);
    setWeekError(null);
    try {
      await api.standby.clearWeek(weekEditor.week.weekStarting);
      setWeekEditor(null);
      await load();
    } catch (err) {
      setWeekError(errText(err, "Could not clear that week. Please try again."));
    } finally {
      setSavingWeek(false);
    }
  }

  async function removeRota() {
    setRemoving(true);
    setRemoveError(null);
    try {
      await api.standby.deleteRota();
      setConfirmRemoveRota(false);
      await load();
    } catch (err) {
      setRemoveError(errText(err, "Could not remove the rota. Please try again."));
    } finally {
      setRemoving(false);
    }
  }

  const thisWeek = data?.weeks.find((w) => w.weekStarting === data.thisWeek) ?? null;
  const engineerName = (id: string) => data?.engineers.find((e) => e.id === id)?.name ?? "Unknown engineer";

  return (
    <div>
      <PageHeader
        className="mb-4"
        title="Standby rota"
        subtitle="Who is on call each week, set in advance. Choose the engineers and the order once and the rota carries on by itself; change any single week when somebody is away, without disturbing the rest of it."
        actions={
          state === "ready" ? (
            <>
              {/* Session ATU: the office's advance notice to the engineers. Opens a preview naming every
                  recipient; nothing is sent until Send is pressed there. */}
              <button
                onClick={() => setSendOpen(true)}
                className="inline-flex items-center justify-center gap-1.5 rounded-ele border border-ele-purple-accent px-5 py-2.5 font-heading text-sm tracking-wide text-ele-body transition hover:bg-ele-inset hover:text-ele-strong focus:outline-none focus:ring-2 focus:ring-ele-green max-sm:min-h-11"
              >
                <Mail aria-hidden size={15} />
                Send rota to engineers
              </button>
              <Button onClick={startRotaEdit} icon={<Pencil aria-hidden size={15} />}>
                {data?.rota ? "Change the rota" : "Set up the rota"}
              </Button>
            </>
          ) : undefined
        }
      />

      {state === "loading" ? (
        <Loader label="Loading the standby rota..." />
      ) : state === "error" ? (
        <p role="alert" className="rounded-ele border border-ele-purple-accent bg-ele-surface p-6 text-center text-ele-strong">
          Could not load the standby rota. Please try again in a moment.
        </p>
      ) : !data ? null : (
        <>
          {/* Who is on call RIGHT NOW, in the largest type on the page. This is the whole reason the
              page exists, so it is not a row in the table below that has to be found. */}
          <div className="mb-5 flex items-start gap-3 rounded-ele border border-ele-purple-accent bg-ele-surface p-4">
            <CalendarClock aria-hidden size={20} className="mt-1 text-ele-green" />
            <div>
              <p className="font-menu text-xs uppercase tracking-widest text-ele-blue">On standby this week</p>
              <p className="mt-1 font-heading text-2xl tracking-wide text-ele-strong">
                {thisWeek ? standbyEngineerLabel(thisWeek) : "Nobody on standby"}
              </p>
              <p className="mt-1 text-xs text-ele-body">
                Week beginning {formatDate(data.thisWeek)}
                {thisWeek?.source === "override" ? ". Set for this week by the office." : thisWeek?.source === "rota" ? ". From the rota." : ". No rota covers this week."}
                {thisWeek?.reason ? ` ${thisWeek.reason}` : ""}
              </p>
            </div>
          </div>

          {data.rota ? (
            <div className="mb-5 rounded-ele border border-ele-purple-accent bg-ele-surface p-4 text-sm">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h2 className="font-heading text-lg text-ele-blue">The rotation</h2>
                  <p className="mt-1 text-ele-body">
                    {data.rota.members.map((m) => m.engineerName).join(", then ")}. Each engineer is on
                    standby for {data.rota.weeksPerTurn === 1 ? "one week" : `${data.rota.weeksPerTurn} weeks`} at a
                    time, starting from {formatDate(data.rota.startWeek)}. {cycleLabel(data.rota.cycleWeeks)}
                  </p>
                  {data.rota.notes && <p className="mt-2 text-xs text-ele-body">{data.rota.notes}</p>}
                </div>
                <button
                  onClick={() => {
                    setRemoveError(null);
                    setConfirmRemoveRota(true);
                  }}
                  className="rounded-ele border border-ele-purple-accent px-4 py-2 text-sm text-ele-body transition hover:border-ele-danger hover:text-ele-danger max-sm:min-h-11"
                >
                  Remove the rota
                </button>
              </div>
            </div>
          ) : (
            <div className="mb-5 rounded-ele border border-ele-purple-accent bg-ele-surface p-4 text-sm">
              <h2 className="font-heading text-lg text-ele-blue">No rota set up yet</h2>
              <p className="mt-2 max-w-3xl text-ele-body">
                Without a rota, no week has anyone on standby unless you set that week by hand below.
                Set the rota up once and every week from its start date onwards has a name against it.
              </p>
            </div>
          )}

          {mobileCards ? (
            /* Below md the four-column table cannot hold the action beside the week. Each week is a
               card: the date and the name lead, "Set by" (hidden on a phone before) is shown, and
               Change this week sits on its own 44px row. */
            <MobileCardList label="Standby weeks">
              {data.weeks.map((w) => (
                <MobileListCard
                  key={w.weekStarting}
                  primary={formatDate(w.weekStarting)}
                  secondary={standbyEngineerLabel(w)}
                  meta={<SetByDetails week={w} thisWeek={data.thisWeek} />}
                  right={w.weekStarting === data.thisWeek ? <StatusPill word="This week" tone="green" /> : undefined}
                  actions={<ChangeWeekButton onClick={() => openWeekEditor(w)} />}
                />
              ))}
            </MobileCardList>
          ) : (
          <div className="overflow-hidden rounded-ele border border-ele-purple-accent">
            <table className="ele-list-table w-full text-left text-sm">
              <colgroup>
                <col style={{ width: "30%" }} />
                <col style={{ width: "28%" }} />
                <col className="max-md:hidden" style={{ width: "26%" }} />
                <col style={{ width: "16%" }} />
              </colgroup>
              <thead className="bg-ele-inset font-menu text-xs uppercase tracking-widest text-ele-blue">
                <tr>
                  <th className="px-3 py-2">Week</th>
                  <th className="px-3 py-2">On standby</th>
                  <th className="max-md:hidden px-3 py-2">Set by</th>
                  <th className="cell-actions px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.weeks.map((w) => (
                  <tr key={w.weekStarting} className="border-t border-ele-purple-accent">
                    <td className="px-3 py-2 align-top">
                      <span className="text-ele-strong">{formatDate(w.weekStarting)}</span>
                      {w.weekStarting === data.thisWeek && <span className="ml-2"><StatusPill word="This week" tone="green" /></span>}
                    </td>
                    <td className="px-3 py-2 align-top text-ele-strong">{standbyEngineerLabel(w)}</td>
                    <td className="max-md:hidden px-3 py-2 align-top text-ele-body">
                      <SetByDetails week={w} thisWeek={data.thisWeek} />
                    </td>
                    <td className="cell-actions px-3 py-2 text-right align-top">
                      <ChangeWeekButton onClick={() => openWeekEditor(w)} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}

          <p className="mt-3 max-w-3xl text-xs text-ele-body">
            The standby retainer on a subcontractor engineer's week follows this rota: a week with
            their name on it counts as a standby week on their timesheet automatically. The tick box
            on a timesheet is still there for the exception, where somebody covered unplanned.
          </p>
        </>
      )}

      {rotaEditor && (
        <ModalShell ariaLabel="Set the standby rota" onClose={() => !savingRota && setRotaEditor(null)} width="max-w-[560px]">
          <form onSubmit={saveRota}>
            <h2 className="mb-1 font-heading text-xl tracking-wide text-ele-strong">
              {data?.rota ? "Change the standby rota" : "Set up the standby rota"}
            </h2>
            <p className="mb-4 text-xs text-ele-body">
              The engineers take standby in the order below, then it starts again at the top.
            </p>

            <div className="grid gap-3 sm:grid-cols-2">
              <label className="block">
                <span className={fieldLabelCls}>Starts from</span>
                <input
                  type="date"
                  value={rotaEditor.startWeek}
                  onChange={(e) => setRotaEditor((f) => (f ? { ...f, startWeek: e.target.value } : f))}
                  className={fieldInputCls}
                />
                <span className="mt-1 block text-xs text-ele-body">
                  Any day in the first week. Weeks before this one are left alone: the rota never
                  claims a week that happened before it existed.
                </span>
              </label>
              <label className="block">
                <span className={fieldLabelCls}>Weeks each engineer is on for</span>
                <input
                  type="number"
                  min={1}
                  step={1}
                  inputMode="numeric"
                  value={rotaEditor.weeksPerTurn}
                  onChange={(e) => setRotaEditor((f) => (f ? { ...f, weeksPerTurn: e.target.value } : f))}
                  className={fieldInputCls}
                />
                <span className="mt-1 block text-xs text-ele-body">1 for a weekly rota, 2 for fortnightly, 4 for four-weekly.</span>
              </label>
            </div>

            <div className="mt-4">
              <span className={fieldLabelCls}>Who is in the rotation, in order</span>
              <ul className="rounded-ele border border-ele-purple-accent">
                {rotaEditor.engineerIds.map((id, i) => (
                  <li key={id} className="flex items-center justify-between gap-2 border-b border-ele-purple-accent px-3 py-2 last:border-b-0">
                    <span className="text-sm text-ele-strong">
                      <span className="font-menu text-xs text-ele-body">{i + 1}.</span> {engineerName(id)}
                    </span>
                    <span className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => moveMember(id, -1)}
                        disabled={i === 0}
                        aria-label={`Move ${engineerName(id)} earlier in the rotation`}
                        className="rounded-ele border border-ele-purple-accent px-2 py-1 text-xs text-ele-body transition hover:border-ele-green hover:text-ele-green disabled:opacity-40"
                      >
                        Up
                      </button>
                      <button
                        type="button"
                        onClick={() => moveMember(id, 1)}
                        disabled={i === rotaEditor.engineerIds.length - 1}
                        aria-label={`Move ${engineerName(id)} later in the rotation`}
                        className="rounded-ele border border-ele-purple-accent px-2 py-1 text-xs text-ele-body transition hover:border-ele-green hover:text-ele-green disabled:opacity-40"
                      >
                        Down
                      </button>
                      <button
                        type="button"
                        onClick={() => toggleMember(id)}
                        aria-label={`Take ${engineerName(id)} out of the rotation`}
                        className="rounded-ele border border-ele-purple-accent px-2 py-1 text-xs text-ele-body transition hover:border-ele-danger hover:text-ele-danger"
                      >
                        Remove
                      </button>
                    </span>
                  </li>
                ))}
                {rotaEditor.engineerIds.length === 0 && <li className="px-3 py-2 text-sm text-ele-body">Nobody chosen yet.</li>}
              </ul>

              {(data?.engineers ?? []).filter((e) => !rotaEditor.engineerIds.includes(e.id)).length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {(data?.engineers ?? [])
                    .filter((e) => !rotaEditor.engineerIds.includes(e.id))
                    .map((e) => (
                      <button
                        key={e.id}
                        type="button"
                        onClick={() => toggleMember(e.id)}
                        className="rounded-ele border border-ele-purple-accent px-3 py-1.5 text-xs text-ele-body transition hover:border-ele-green hover:text-ele-green"
                      >
                        Add {e.name}
                      </button>
                    ))}
                </div>
              )}
            </div>

            <label className="mt-4 block">
              <span className={fieldLabelCls}>Note (optional)</span>
              <input
                value={rotaEditor.notes}
                onChange={(e) => setRotaEditor((f) => (f ? { ...f, notes: e.target.value } : f))}
                placeholder="Agreed with the engineers on 5 September"
                className={fieldInputCls}
              />
            </label>

            {rotaError && (
              <p role="alert" className="mt-3 rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">
                {rotaError}
              </p>
            )}

            <div className={`mt-5 gap-3 ${MODAL_ACTIONS}`}>
              <button type="button" onClick={() => setRotaEditor(null)} disabled={savingRota} className={MODAL_SECONDARY_BTN}>
                Cancel
              </button>
              <button type="submit" disabled={savingRota} className={MODAL_PRIMARY_BTN}>
                {savingRota ? "Saving..." : "Save the rota"}
              </button>
            </div>
          </form>
        </ModalShell>
      )}

      {weekEditor && (
        <ModalShell ariaLabel="Change one week's standby" onClose={() => !savingWeek && setWeekEditor(null)} width="max-w-[480px]">
          <form onSubmit={saveWeek}>
            <h2 className="mb-1 font-heading text-xl tracking-wide text-ele-strong">
              Week beginning {formatDate(weekEditor.week.weekStarting)}
            </h2>
            <p className="mb-4 text-xs text-ele-body">
              This changes this week only. The rota carries on unchanged either side of it.
            </p>

            <label className="block">
              <span className={fieldLabelCls}>On standby this week</span>
              <select
                value={weekEditor.engineerId}
                onChange={(e) => setWeekEditor((f) => (f ? { ...f, engineerId: e.target.value } : f))}
                className={fieldInputCls}
              >
                <option value="">Nobody on standby</option>
                {(data?.engineers ?? []).map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.name}
                  </option>
                ))}
              </select>
            </label>

            <label className="mt-3 block">
              <span className={fieldLabelCls}>Why (optional)</span>
              <input
                value={weekEditor.reason}
                onChange={(e) => setWeekEditor((f) => (f ? { ...f, reason: e.target.value } : f))}
                placeholder="e.g. On leave, a colleague covering"
                className={fieldInputCls}
              />
            </label>

            {weekError && (
              <p role="alert" className="mt-3 rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">
                {weekError}
              </p>
            )}

            <div className={`mt-5 gap-3 ${MODAL_ACTIONS}`}>
              {weekEditor.week.source === "override" && (
                <button
                  type="button"
                  onClick={() => void clearWeek()}
                  disabled={savingWeek}
                  className={MODAL_SECONDARY_BTN}
                >
                  Back to the rota
                </button>
              )}
              <button type="button" onClick={() => setWeekEditor(null)} disabled={savingWeek} className={MODAL_SECONDARY_BTN}>
                Cancel
              </button>
              <button type="submit" disabled={savingWeek} className={MODAL_PRIMARY_BTN}>
                {savingWeek ? "Saving..." : "Save this week"}
              </button>
            </div>
          </form>
        </ModalShell>
      )}

      {sendOffer && (
        <ModalShell ariaLabel="Send the rota to the engineers now?" onClose={() => setSendOffer(false)} width="max-w-[440px]">
          <h2 className="mb-1 font-heading text-xl tracking-wide text-ele-strong">Rota saved. Send it to the engineers now?</h2>
          <p className="text-sm text-ele-body">
            You will see who it goes to before anything is sent. Nothing is sent if you choose Not now; you can send it later from this page.
          </p>
          <div className={`mt-5 gap-3 ${MODAL_ACTIONS}`}>
            <button type="button" onClick={() => setSendOffer(false)} className={MODAL_SECONDARY_BTN}>
              Not now
            </button>
            <button
              type="button"
              onClick={() => {
                setSendOffer(false);
                setSendOpen(true);
              }}
              className={MODAL_PRIMARY_BTN}
            >
              Send now
            </button>
          </div>
        </ModalShell>
      )}

      {sendOpen && <StandbyRotaSendModal onClose={() => setSendOpen(false)} />}

      <ConfirmDeleteModal
        open={confirmRemoveRota}
        title="Remove the standby rota"
        message="The rotation is removed and no future week has anyone on standby until a new rota is set up. Weeks you have set by hand are kept. No timesheet or pay record is changed."
        confirmLabel="Remove the rota"
        busy={removing}
        error={removeError}
        onConfirm={() => void removeRota()}
        onCancel={() => setConfirmRemoveRota(false)}
      />
    </div>
  );
}
